// ============================================================
//  CraftingGrid.js  —  SimpleMC
//
//  Exports:
//    McIcon              — renders a MC item/block texture by key
//    CraftingGrid        — read-only 3×3 display with counts (Wiki)
//    CraftingGridPicker  — editable 3×3 grid with counts (Admin)
//
//  Slot format (internal): { key: string|null, count: number }
//  Textures: /mc-icons/item/{key}.png → /mc-icons/block/{key}.png
// ============================================================

import React, { useState, useEffect, useRef } from 'react';
import './CraftingGrid.css';
import MC_ITEMS from '../data/mc_items';

// ── Default quick-pick palette ───────────────────────────────
const QUICKPICKS = [
  'stick', 'oak_planks', 'cobblestone', 'iron_ingot', 'gold_ingot',
  'diamond', 'netherite_ingot', 'string', 'feather', 'blaze_rod',
  'ender_pearl', 'obsidian', 'leather', 'bone', 'coal',
  'redstone', 'lapis_lazuli', 'emerald', 'quartz',
];

// ── Normalise any slot format → { key, count } ──────────────
function normaliseSlot(s) {
  if (!s) return { key: null, count: 1 };
  if (typeof s === 'string') return { key: s, count: 1 };
  return { key: s.key ?? null, count: s.count ?? 1 };
}

// ── Convert recipe object / array → array of 9 slot objects ─
export function slotsToArray(recipe) {
  if (!recipe) return Array(9).fill(null).map(() => ({ key: null, count: 1 }));
  if (Array.isArray(recipe)) return recipe.map(normaliseSlot);
  // DB object: { slot_1, slot_1_count, … slot_9, slot_9_count }
  return Array.from({ length: 9 }, (_, i) => ({
    key: recipe[`slot_${i + 1}`] ?? null,
    count: recipe[`slot_${i + 1}_count`] ?? 1,
  }));
}

// ── McIcon ───────────────────────────────────────────────────
export function McIcon({ materialKey, size = 36 }) {
  const [fallback, setFallback] = useState(0);
  useEffect(() => { setFallback(0); }, [materialKey]);

  const normalizedKey = String(materialKey ?? '')
    .trim()
    .toLowerCase()
    .replace(/^minecraft:/, '')
    .replace(/[^a-z0-9_]/g, '');
  if (!normalizedKey) return <div style={{ width: size, height: size }} />;

  const ICON_CACHE_BUST = '20260424';
  const sources = [
    `/mc-icons/${normalizedKey}.png?v=${ICON_CACHE_BUST}`,
  ];

  if (fallback >= sources.length) {
    return (
      <McIcon materialKey="BARRIER" size={size} />
    );
  }

  return (
    <img
      className="mc-icon"
      src={sources[fallback]}
      alt={materialKey}
      width={size}
      height={size}
      onError={() => setFallback(f => f + 1)}
      title={materialKey}
    />
  );
}

// ── CraftingGrid — read-only display (Wiki) ──────────────────
// items prop is optional: used to resolve custom item slugs → material icon key
export function CraftingGrid({ slots, outputKey, outputCount = 1, note, items = [] }) {
  const arr = slotsToArray(slots);

  // If key is a custom item slug, return its material; otherwise return key as-is
  function iconKey(key) {
    if (!key) return null;
    const custom = items.find(i => i.slug === key);
    return custom?.material || key;
  }

  return (
    <div>
      <div className="craft-grid-display">
        {/* 3×3 grid */}
        <div className="craft-grid-3x3">
          {arr.map((slot, i) => {
            const label = slot.key
              ? (items.find(it => it.slug === slot.key)?.name ?? slot.key.replace(/_/g, ' '))
              : null;
            return (
              <div key={i} className="craft-slot" data-tooltip={label}>
                {slot.key && <McIcon materialKey={iconKey(slot.key)} size={36} />}
                {slot.key && slot.count > 1 && (
                  <span className="craft-slot-count">{slot.count}</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="craft-arrow">→</div>

        {/* Output */}
        <div className="craft-output">
          {outputKey && <McIcon materialKey={iconKey(outputKey)} size={44} />}
          {outputCount > 1 && (
            <span className="craft-slot-count craft-slot-count-lg">{outputCount}</span>
          )}
        </div>
      </div>

      {note && <div className="craft-note"><McIcon materialKey="knowledge_book" size={13} /> {note}</div>}
    </div>
  );
}

export function CraftingGridPicker({
  slots,
  onChange,
  outputKey,
  outputCount = 1,
  onOutputCountChange,
  customItems = [],
}) {
  const arr = slotsToArray(slots);

  const [activeSlot, setActiveSlot] = useState(null);
  const [query, setQuery] = useState('');
  const [count, setCount] = useState(1);
  const inputRef = useRef(null);

  useEffect(() => {
    if (activeSlot != null) inputRef.current?.focus();
  }, [activeSlot]);

  const customKeys  = customItems.map(i => ({ key: i.slug, label: i.name, material: i.material || i.slug, custom: true }));
  const vanillaKeys = MC_ITEMS.map(k => ({ key: k, label: k, material: k, custom: false }));
  const allKeys     = [...customKeys, ...vanillaKeys];

  const isFiltering = query.length >= 2;
  const filtered = isFiltering
    ? allKeys
        .filter(i => i.key?.toLowerCase().includes(query.toLowerCase())
                  || i.label?.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 24)
    : null;

  // Resolve slot key → display label
  function resolveLabel(key) {
    if (!key) return null;
    const custom = customItems.find(i => i.slug === key);
    return custom ? custom.name : key.replace(/_/g, ' ');
  }

  // Resolve slot key → icon material key
  function resolveIconKey(key) {
    if (!key) return null;
    const custom = customItems.find(i => i.slug === key);
    return custom?.material || key;
  }

  function handleSlotClick(i) {
    if (activeSlot === i) {
      setActiveSlot(null); setQuery(''); setCount(1);
    } else {
      setActiveSlot(i);
      setQuery(arr[i].key ?? '');
      setCount(arr[i].count ?? 1);
    }
  }

  function clearSlot(i, e) {
    e.stopPropagation();
    const next = [...arr];
    next[i] = { key: null, count: 1 };
    onChange(next);
    if (activeSlot === i) { setActiveSlot(null); setQuery(''); setCount(1); }
  }

  function applyQuery(key, qty) {
    const val = (key ?? query).trim() || null;
    if (activeSlot == null) return;
    const next = [...arr];
    next[activeSlot] = { key: val, count: Math.min(64, Math.max(1, qty ?? count)) };
    onChange(next);
    setActiveSlot(null); setQuery(''); setCount(1);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') applyQuery();
    if (e.key === 'Escape') { setActiveSlot(null); setQuery(''); setCount(1); }
  }

  function clampCount(val) {
    const n = parseInt(val, 10);
    if (isNaN(n)) return 1;
    return Math.min(64, Math.max(1, n));
  }

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="craft-picker">
      <div className="craft-picker-top">

        {/* 3×3 editable grid */}
        <div className="craft-grid-3x3">
          {arr.map((slot, i) => (
            <button
              key={i}
              type="button"
              className={`craft-slot-btn${activeSlot === i ? ' active' : ''}${slot.key ? ' filled' : ''}`}
              onClick={() => handleSlotClick(i)}
              title={slot.key ? resolveLabel(slot.key) : `Slot ${i + 1}`}
              data-tooltip={slot.key ? resolveLabel(slot.key) : null}
            >
              {slot.key
                ? <McIcon materialKey={resolveIconKey(slot.key)} size={34} />
                : activeSlot === i
                  ? <span style={{ fontSize: '0.55rem', color: 'var(--teal)', fontWeight: 700 }}>SET</span>
                  : null
              }
              {slot.key && slot.count > 1 && (
                <span className="craft-slot-count">{slot.count}</span>
              )}
              {slot.key && (
                <button type="button" className="craft-slot-clear" onClick={e => clearSlot(i, e)}>×</button>
              )}
            </button>
          ))}
        </div>

        <div className="craft-arrow" style={{ fontSize: '1.5rem', color: 'var(--text-muted)' }}>→</div>

        {/* Output slot */}
        <div className="craft-picker-output">
          <div className="craft-picker-output-slot">
            {outputKey && <McIcon materialKey={outputKey} size={44} />}
            {outputCount > 1 && (
              <span className="craft-slot-count craft-slot-count-lg">{outputCount}</span>
            )}
          </div>
          {onOutputCountChange && (
            <div className="craft-output-count-row">
              <span className="craft-count-label">Qty</span>
              <input
                className="craft-count-input"
                value={outputCount}
                onChange={e => onOutputCountChange(e.target.value)}
                onBlur={e => onOutputCountChange(clampCount(e.target.value))}
                title="Output quantity (1–64)"
                maxLength={2}
              />
            </div>
          )}
          <span className="craft-picker-output-label">Output</span>
        </div>
      </div>

      {/* Slot editor */}
      {activeSlot != null && (
        <div className="craft-slot-editor">
          <div className="craft-slot-editor-header">
            <span className="craft-slot-editor-label">Slot {activeSlot + 1}</span>
            <button type="button" className="craft-slot-cancel-btn"
              onClick={() => { setActiveSlot(null); setQuery(''); setCount(1); }}>
              Esc
            </button>
          </div>

          {/* Material key + count */}
          <div className="craft-slot-input-row">
            <div className="craft-slot-preview">
              <McIcon materialKey={query} size={28} />
            </div>
            <input
              ref={inputRef}
              className="craft-slot-input"
              placeholder="e.g. iron_ingot, oak_planks…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="craft-count-wrap">
              <span className="craft-count-label">×</span>
              <input
                className="craft-count-input"
                value={count}
                onChange={e => setCount(e.target.value)}
                onBlur={e => setCount(clampCount(e.target.value))}
                title="Amount (1–64)"
                maxLength={2}
              />
            </div>
            <button type="button" className="craft-slot-set-btn" onClick={() => applyQuery()}>
              Set
            </button>
          </div>

          {/* Filtered results or default quick-picks */}
          <div className="craft-quickpick">
            {isFiltering ? (
              <>
                <span className="craft-quickpick-label">
                  {filtered.length} result{filtered.length !== 1 ? 's' : ''}
                </span>
                {filtered.length === 0 ? (
                  <span style={{ fontSize: 'var(--t-xs)', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    No matches — press Set to use as-is
                  </span>
                ) : filtered.map(({ key, label, material, custom }) => (
                  <button
                    key={key}
                    type="button"
                    className={`craft-quickpick-btn${custom ? ' craft-quickpick-custom' : ' craft-quickpick-vanilla'}`}
                    data-tooltip={custom ? `${label} ★` : label.replace(/_/g, ' ')}
                    onClick={() => applyQuery(key)}
                  >
                    <div className="craft-quickpick-icon-wrap">
                      <McIcon materialKey={material} size={22} />
                      {custom && <span className="craft-custom-dot" />}
                    </div>
                  </button>
                ))}
              </>
            ) : (
              <>
                <span className="craft-quickpick-label">Quick pick</span>
                {QUICKPICKS.map(key => (
                  <button key={key} type="button" className="craft-quickpick-btn" title={key}
                    onClick={() => applyQuery(key)}>
                    <McIcon materialKey={key} size={22} />
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      <p className="craft-picker-hint">
        Click a slot to assign · Set amount (max 64) · × to clear · Enter to confirm
      </p>
    </div>
  );
}

// ── ItemPickerInput ──────────────────────────────────────────
// Standalone item search + icon-grid picker (same UX as crafting slot editor).
// `value` / `onChange`: the stored name string (custom item display name or vanilla key).
// `customItems`: array of item objects with { name, material, slug }.
export function ItemPickerInput({ value, onChange, customItems = [], placeholder = 'Pick an item…' }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const inputRef          = useRef(null);
  const wrapRef           = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setQuery(''); } }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); }, [open]);

  const customKeys  = customItems.map(i => ({ key: i.name,  label: i.name,  material: i.material || i.slug, custom: true  }));
  const vanillaKeys = MC_ITEMS.map(k =>    ({ key: k,        label: k,        material: k,                    custom: false }));
  const allKeys     = [...customKeys, ...vanillaKeys];

  const isFiltering = query.length >= 2;
  const filtered    = isFiltering
    ? allKeys.filter(i => i.label.toLowerCase().includes(query.toLowerCase())).slice(0, 30)
    : null;

  // Resolve icon for whatever is currently stored
  const currentCustom = customItems.find(i => i.name === value);
  const displayMaterial = currentCustom?.material || (value || '').toLowerCase().replace(/\s+/g, '_');

  function select(label) {
    onChange(label);
    setOpen(false);
    setQuery('');
  }

  return (
    <div className="ipi-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`ipi-trigger${open ? ' open' : ''}`}
        onClick={() => { setOpen(o => !o); setQuery(value || ''); }}
      >
        {value
          ? <><McIcon materialKey={displayMaterial} size={20} /><span className="ipi-trigger-name">{value}</span></>
          : <span className="ipi-trigger-placeholder">{placeholder}</span>
        }
        {value && (
          <span className="ipi-clear" onClick={e => { e.stopPropagation(); onChange(''); setOpen(false); }}>×</span>
        )}
        <span className="ipi-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="ipi-dropdown">
          <div className="craft-slot-input-row" style={{ padding: '0.5rem 0.6rem 0' }}>
            <div className="craft-slot-preview">
              <McIcon materialKey={query.toLowerCase().replace(/\s+/g, '_')} size={24} />
            </div>
            <input
              ref={inputRef}
              className="craft-slot-input"
              placeholder="Search vanilla or custom items…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && query.trim()) select(query.trim()); if (e.key === 'Escape') { setOpen(false); setQuery(''); } }}
            />
          </div>
          <div className="craft-quickpick" style={{ padding: '0.4rem 0.6rem 0.6rem' }}>
            {isFiltering ? (
              <>
                <span className="craft-quickpick-label">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
                {filtered.length === 0
                  ? <span style={{ fontSize: 'var(--t-xs)', color: 'var(--text-muted)', fontStyle: 'italic' }}>No matches — press Enter to use as-is</span>
                  : filtered.map(({ key, label, material, custom }) => (
                    <button
                      key={key}
                      type="button"
                      className={`craft-quickpick-btn${custom ? ' craft-quickpick-custom' : ' craft-quickpick-vanilla'}`}
                      data-tooltip={custom ? `${label} ★` : label.replace(/_/g, ' ')}
                      onClick={() => select(label)}
                    >
                      <div className="craft-quickpick-icon-wrap">
                        <McIcon materialKey={material} size={22} />
                        {custom && <span className="craft-custom-dot" />}
                      </div>
                    </button>
                  ))
                }
              </>
            ) : (
              <>
                <span className="craft-quickpick-label">Quick pick</span>
                {QUICKPICKS.map(k => (
                  <button key={k} type="button" className="craft-quickpick-btn" data-tooltip={k} onClick={() => select(k)}>
                    <McIcon materialKey={k} size={22} />
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
